module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('section', {
    staticClass: "chapter",
    attrs: {
      "epub:type": "chapter",
      "id": "ch02"
    }
  }, [_c('h1', {
    staticClass: "cn"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p6"
    }
  }), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0006#toc"
    }
  }, [_c('span', {
    staticClass: "sm"
  }, [_vm._v("TWO")])])]), _vm._v(" "), _c('h1', {
    staticClass: "ct"
  }, [_vm._v("The Coevolution of Affect toward Presidents and Parties")]), _vm._v(" "), _c('p', {
    staticClass: "pf"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p6"
    }
  }), _vm._v("The central concern of this book is the diverse ways in which presidents have shaped the public standing, reputation, and electoral success of their parties during the postwar era. But attitudes toward prospective presidents and their parties are already closely entwined by the time they take office. Most Americans hold preformed opinions of the parties that guide reactions to presidential candidates, among them all future presidents, as soon as they have arrived on the national stage tagged with a party label. And in the course of winning nominations and presenting themselves and their ideas to the public, prospective presidents have already begun to influence how people feel and think about their parties.")]), _vm._v(" "), _c('p', [_vm._v("In this chapter I examine the ways in which attitudes toward presidents and their parties come to be linked in the public mind before they are elected, during their time in office, and after they are no longer on the ballot and new candidates emerge to take up their party’s banner. Constrained by the available data, my focus here is on the affective component of political evaluations: how partisanship influences expressed feelings about presidential candidates and presidents, and how feelings about these leaders in turn influence feelings about their parties, over the course of electoral careers. To anticipate, I find clear and consistent evidence that links between party and candidate affect are comparatively modest at first, increase sharply during the initial campaign for the White House for both candidates, tighten further when the winner seeks reelection, and then atrophy once the president leaves office. In this sequence, the causal arrow runs first from party affect (manifest in party identification) to candidate affect but gradually shifts direction to run from candidate or president affect to party affect (and party identification). I also find that the relative impact on party affect of the president and prospective or actual successors within the same party varies "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p7"
    }
  }), _vm._v("over time in a way that underlines the president’s leading role in shaping popular feelings about his party.")]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0201"
    }
  }, [_vm._v("Theoretical Considerations")]), _vm._v(" "), _c('p', {
    staticClass: "paft"
  }, [_vm._v("The links between people’s expressed opinions and beliefs about parties, presidential candidates, and presidents explored in this chapter and throughout this book are measured by how they respond to questions posed by survey researchers. It is thus essential to consider the mental processes that generate individuals’ survey responses. The constructivist prospective provides clearest framework for interpreting the patterns that emerge from the data. According to Zaller and Feldman’s well-known model, most citizens do not hold preformed attitudes that they simply reveal when questioned in a survey; “rather they carry around in their heads a mix of only partially consistent ideas and considerations. When questioned, they call to mind a sample of these ideas, including an oversample of ideas made salient by the questionnaire and other recent events, and use them to choose among the options offered. But their choices do not, in most cases, reflect anything that can be described as true attitudes; rather, they reflect the thoughts that are most accessible in memory at the moment of response” (1992, 579–80).")]), _vm._v(" "), _c('p', [_vm._v("Lodge and Taber accept this process with the proviso that the stored considerations that go into constructing expressed opinions are inherently affect-laden: “all political leaders, groups, issues, symbols, and ideas thought about and evaluated in the past become affectively tagged—positively, negatively or both” (2013, 43). The “affective tags are attached when an object (person, group, place, event, issue, or abstract concept) is first evaluated and strengthened with each replication. . . . After but one or two evaluations, a concept is ‘hot,’ affectively charged” (53). A running tally, “representing an automatic integration of all prior evaluations of the object, is then restored to long-term memory where it is readily available for subsequent evaluations” (51). Thus “the evaluations a citizen might report in an opinion poll . . . reflect the integration of thoughts and feelings associated with . . . conscious and unconscious political evaluations. Immediately and without intentional control, a perceived candidate, issue, group, or idea is classified as either good or bad” (44).")]), _vm._v(" "), _c('p', [_vm._v("In this process, political objects and their associated cognitions are inevitably subject to motivated reasoning (to Lodge and Taber, “"), _c('i', [_vm._v("all reasoning is motivated")]), _vm._v("” [emphasis theirs, 2013, 150]). “Affective experience feeds back on the appraisal process itself, routinely promoting a selective search of long-term memory for belief-confirming evidence and thereby informing "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p8"
    }
  }), _vm._v("the conscious expression of opinion, choice, and action in affectively-congruent ways” (207). Motivated reasoning occurs only with motivation, of course, which is readily supplied by being questioned in a survey that, for example, primes partisan political considerations: “priming is the key mechanism guiding the activation process as these associations [between thoughts, feelings, and beliefs]—weighted by one’s experience—become the considerations that spontaneously inform both conscious and unconscious judgment and choice” (207).")]), _vm._v(" "), _c('p', [_vm._v("This perspective provides a basis for understanding a host of survey-based findings reported throughout this book. For the present chapter, it suggests several hypotheses regarding how the relationships between opinions about presidential candidates and presidents and opinions about their parties should develop and vary over time. First, expressed opinions of political figures will be conditioned by partisan priors. Party identification, the single most consistent organizer of political attitudes and behavior, should be especially potent if it is indeed a central component of an individual’s social and personal identity, as the classic Michigan model and its more recent defenders posit (Campbell et al. 1960; Green, Palmquist, and Schickler 2002). Insofar as people identify with a party—which a large majority of American do"), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chijacobson0023#fn1",
      "id": "fn1r"
    }
  }, [_vm._v("1")]), _vm._v("—they will reflexively evaluate politicians with a known party affiliation from a biased perspective (Miller and Shanks 1996). (I will have much more to say about party identification in chapters "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0015#ch06"
    }
  }, [_vm._v("6")]), _vm._v(" and "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0016#ch07"
    }
  }, [_vm._v("7")]), _vm._v(".)")]), _vm._v(" "), _c('p', [_vm._v("Second, however, the link between opinions of parties and politicians should vary in strength and direction with salience, repetition, and context. The relationship should be weaker for the more obscure and peripheral political figures than for those who become presidential candidates or presidents, because the simultaneous activation of affectively tagged considerations pertaining to the political figures and parties will be less common. For the same reason, the links should also be relatively weaker for future presidential candidates in the years before they win the nomination. Moreover, the direction of influence should be primarily from party identification, which draws on comparatively dense networks of affectively tagged memories, to affect toward the political figure, thinly if at all embedded in long-term memory.")]), _vm._v(" "), _c('p', [_vm._v("The relationship should be much stronger for presidential nominees and presidents, and stronger still in the campaign context, which primes and reinforces partisan sentiments across the board (Erikson and Wlezien 2012). "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p9"
    }
  }), _vm._v("Once well on the road to nomination, presidential candidates not only will be evaluated in more explicitly partisan terms but will also begin to have a reciprocal effect on how people feel about their parties. The party convention obviously puts a big official partisan stamp on the nominee, but by its choice (as well as in its formal platform and convention rhetoric) the party also begins to redefine itself, occasionally rather sharply, as did, for example, the Republicans with Barry Goldwater in 1964 and Donald Trump in 2016 or the Democrats with George McGovern in 1972 and Bill Clinton in 1992. The nominee becomes the party’s dominant public face; what people come to think of him or her during the course of the campaign will inevitably color their feelings about the party that did the nominating.")]), _vm._v(" "), _c('p', [_vm._v("Campaigns continually reinforce partisan thinking. As Lodge and Taber put it, first abstractly and then concretely, “with repeated evaluations an affective tag is linked to a concept and springs to mind spontaneously upon mere exposure to the associated object, thereby signaling the concept’s affective coloration. By election eve, most citizens will have formed impressions of the major candidates, parties, and issues and these feelings will be inescapably activated on their mere mention” (2013, 43). Moreover, “affective reacting . . . systematically guides the encoding, search, retrieval, interpretation, and evaluation of information in ways that promote affectively congruent rationalization effects” (2013, 206). Feelings about candidates and parties should thus reach their maximum convergence nearest to the election season.")]), _vm._v(" "), _c('p', [_vm._v("Third, the relationship between affect toward a party and affect toward a political leader should weaken as the leader’s political centrality fades. The linkage should remain strong for successful presidential candidates throughout most of their time in office (the research findings reported in subsequent chapters thoroughly document this reality). As a president’s second term draws to a close, however, the focus of partisan evaluations will begin to shift to the party’s new nominee, and once presidents are out of office, the affectively loaded political memories people draw on when evaluating them and their parties will surface together less frequently. The same holds to an even greater degree for unsuccessful former presidential candidates, now largely out of the public eye. New political figures and events supplant the old in “top-of-the-head” considerations people use to construct their evaluations of parties in response to pollsters’ inquiries.")]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0202"
    }
  }, [_vm._v("The Data")]), _vm._v(" "), _c('p', {
    staticClass: "paft"
  }, [_vm._v("I draw data to test these conjectures from four sets of surveys that collectively (though not individually) span the years 1956 through 2016:")]), _vm._v(" "), _c('ul', {
    staticStyle: {
      "list-style-type": "none"
    }
  }, [_c('li', {
    staticClass: "ulf",
    staticStyle: {
      "text-indent": "1em"
    }
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p10"
    }
  }), _c('i', [_vm._v("Gallup Polls")]), _vm._v(". At irregular intervals beginning in the 1950s, the Gallup Poll asked respondents to rate the two political parties and various political figures using what Gallup called a “scalometer.” This was the question format:")])]), _vm._v(" "), _c('blockquote', [_c('p', {
    staticClass: "bquls",
    staticStyle: {
      "text-indent": "0"
    }
  }, [_vm._v("Here is an interesting experiment (HAND RESPONDENT SCALOMETER). You notice that the 10 boxes on this card go from the HIGHEST POSITION OF PLUS 5—or someone you like very much—all the way down to the LOWEST POSITION OF MINUS 5—or someone you dislike very much. Please tell me how far up or how far down the scale you would rate the following: [PARTY OR LEADER’S NAME].")])]), _vm._v(" "), _c('ul', {
    staticStyle: {
      "list-style-type": "none"
    }
  }, [_c('li', {
    staticClass: "ulp"
  }, [_vm._v("Responses were then recoded to take values from 1 (highest rating) to 10 (lowest rating), with 11 representing the “don’t knows” (omitted from the analyses reported here.)"), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chijacobson0023#fn2",
      "id": "fn2r"
    }
  }, [_vm._v("2")]), _vm._v(" Scalometer ratings of both parties and of varying sets of leaders were asked in nineteen surveys between 1956 and 1991; all presidential and vice presidential candidates between 1956 and 1984 received at least some coverage, as did a variety of potential nominees, former presidents, and former candidates. Another forty-four surveys gathered scalometer ratings of one or more political figures but omitted the parties; the coverage goes up to 2004 but is much thinner after 1990. These surveys also included Gallup’s standard party identification question. For consistency, I treat it as a 3-point scale, with independents who lean toward a party still considered independents; the leanings of independents were not queried in many of the earlier surveys and are thus unavailable for analysis."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chijacobson0023#fn3",
      "id": "fn3r"
    }
  }, [_vm._v("3")])]), _vm._v(" "), _c('li', {
    staticClass: "ul"
  }, [_c('i', [_vm._v("ANES Time Series.")]), _vm._v(" The American National Election Studies (ANES) surveys from 1978 through 2016 (omitting midterms after 1998) have asked respondents to rate the two parties, presidents, presidential candidates, and other political figures on a “feeling thermometer.” This is the question format:")])]), _vm._v(" "), _c('blockquote', [_c('p', {
    staticClass: "bquls",
    staticStyle: {
      "text-indent": "0"
    }
  }, [_vm._v("I’d like to get your feelings toward some of our political leaders and other people who are in the news these days. I’ll read the name of a person and I’d like you to rate that person using something we call the feeling thermometer. Ratings between 50 degrees and 100 degrees mean that you feel favorable and warm toward the person. Ratings between 0 degrees and 50 degrees mean that you don’t feel favorable toward the person and that you don’t care too much for that person. "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p11"
    }
  }), _vm._v("You would rate the person at the 50 degree mark if you don’t feel particularly warm or cold toward the person.")])]), _vm._v(" "), _c('ul', {
    staticStyle: {
      "list-style-type": "none"
    }
  }, [_c('li', {
    staticClass: "ulp"
  }, [_vm._v("The ANES surveys also include the battery of questions that produce the standard 7-point party identification measure used in analyses of these data."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chijacobson0023#fn4",
      "id": "fn4r"
    }
  }, [_vm._v("4")])]), _vm._v(" "), _c('li', {
    staticClass: "ul",
    staticStyle: {
      "text-indent": "1em"
    }
  }, [_c('i', [_vm._v("ANES Panel Study 2008–2009")]), _vm._v(". The ANES conducted a separate panel study of the 2008 election and its aftermath. In six of the panel’s waves (January, February, June, September. and October 2008 and July 2009), respondents were asked a set of questions prefaced by “We’d like to ask how much you like or dislike various people and groups who have been in the news recently.” The questions took a branching format, first asking whether the respondent liked or disliked the party or politician, then asking whether they liked [disliked] them “a great deal,” “a moderate amount, or “a little.” The responses generated a 7-point scale ranging from liking a great deal to disliking a great deal. The standard ANES party ID question was also ask in the January, September, and October 2008 and July 2009 waves.")]), _vm._v(" "), _c('li', {
    staticClass: "ul",
    staticStyle: {
      "text-indent": "1em"
    }
  }, [_c('i', [_vm._v("The 2000, 2004, and 2008 NAES Surveys")]), _vm._v(". The National Annenberg Election Survey (NAES) conducted rolling cross-sectional surveys on a nearly daily basis over the course of the 2000, 2004, and 2008 election years. These surveys did not ask about the parties, but they elicited favorability ratings of candidates and other political figures and also asked the questions that produce the standard 7-point party identification scale, so they offer insight into how the relationship between party ID and affect toward political figures changes over the course of a campaign. In the 2000 survey, affect was measured by a scale ranging from 0 to 100 (from least to most favorable); in 2004 and 2008, the favorability scale ranged from 0 to 10. For the analyses reported here, the rolling cross-sections were aggregated by month."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chijacobson0023#fn5",
      "id": "fn5r"
    }
  }, [_vm._v("5")])])]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0203"
    }
  }, [_vm._v("Party Identification and Affect toward Presidents and Candidates over Time")]), _vm._v(" "), _c('p', {
    staticClass: "paft"
  }, [_vm._v("How do partisan attitudes influence affective evaluations of eventual presidential candidates and presidents from the time they become prominent enough to attract the attention of pollsters through their electoral careers and beyond? According to the first hypothesis proposed in the previous section, partisan differences should appear the very first time polls ask voters to evaluate a prospective presidential candidate, and they should grow as "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p12"
    }
  }), _vm._v("the candidates become more widely known (and hence more accessible to and associated with the party in working memory). The correlation between party identification and affect toward candidates should thus increase over the course of campaigns. Evidence from all four data sources supports these conjectures. "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0201"
    }
  }, [_vm._v("Figure 2.1")]), _vm._v(" displays the average correlations between party identification and candidate rating on Gallup’s scalometer for twenty presidential candidates and presidents between 1956 and 2004 at various points in their electoral careers. Candidates are distinguished by whether they were relative newcomers"), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chijacobson0023#fn6",
      "id": "fn6r"
    }
  }, [_vm._v("6")]), _vm._v(" or veterans (presidents, former candidates and vice presidents)."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chijacobson0023#fn7",
      "id": "fn7r"
    }
  }, [_vm._v("7")]), _vm._v(" Those appearing in the surveys after they were no longer candidates or in office were also distinguished by whether they were defeated (including defeated presidents) or not."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chijacobson0023#fn8",
      "id": "fn8r"
    }
  }, [_vm._v("8")])]), _vm._v(" "), _c('figure', {
    staticClass: "figure",
    attrs: {
      "id": "fig0201"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('img', {
    attrs: {
      "alt": "",
      "height": "326",
      "src": "images/chi-jacobson-fig02001.jpeg",
      "width": "600"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p12"
    }
  }), _c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 2.1.")]), _vm._v(" Correlations between party identification and candidate/president affect, 1956–2004")]), _vm._v(" "), _c('p', {
    staticClass: "fign"
  }, [_c('i', [_vm._v("Note")]), _vm._v(": Number of correlations averaged for the two columns is in parentheses.")]), _vm._v(" "), _c('p', {
    staticClass: "figatr"
  }, [_c('i', [_vm._v("Source")]), _vm._v(": Gallup Polls.")])]), _vm._v(" "), _c('p', [_vm._v("The sequential patterns are straightforward. Correlations between partisanship and affect for prospective candidates newly on the scene are positive "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p13"
    }
  }), _vm._v("but comparatively modest prior to the election year, when they begin to increase; after Labor Day, they are indistinguishable from those of veteran candidates."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chijacobson0023#fn9",
      "id": "fn9r"
    }
  }, [_vm._v("9")]), _vm._v(" Correlations begin considerably higher for veterans and remain, on average, stable until the post–Labor Day period, when they jump to their highest average levels. The average correlation for sitting presidents in years when they are not involved in pursuing reelection is somewhat lower, and it is lower still once they have left office. For losing candidates, including losing presidents, the postelection correlations are lower still. These patterns hold for most candidates individually; for example, for thirteen of the seventeen, the correlation reaches its highest level in the survey taken closest to the election day."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chijacobson0023#fn10",
      "id": "fn10r"
    }
  }, [_vm._v("10")])]), _vm._v(" "), _c('p', [_vm._v("A similar pattern appears in data from the ANES election-year time series ("), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0202"
    }
  }, [_vm._v("figure 2.2")]), _vm._v("). Correlations between party identification and candidate/president feeling thermometer ratings are lowest in surveys taken prior to the election year in which the candidate first wins the nomination, especially among newcomers,"), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chijacobson0023#fn11",
      "id": "fn11r"
    }
  }, [_vm._v("11")]), _vm._v(" rise to a high point during the initial election contest, fall a bit in years when the president is not on the ballot, and rise again to a high when the president runs for reelection. Correlations are smaller in later election years when the leader is no longer a candidate, especially following defeats. Again, these patterns are generally repeated for individual leaders; for every one of the ten who appear as a candidate in only one election contest, the correlation is highest in the survey for that year; for the seven who appear twice, it is highest in the second survey for six."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chijacobson0023#fn12",
      "id": "fn12r"
    }
  }, [_vm._v("12")])]), _vm._v(" "), _c('figure', {
    staticClass: "figure",
    attrs: {
      "id": "fig0202"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('img', {
    attrs: {
      "alt": "",
      "height": "320",
      "src": "images/chi-jacobson-fig02002.jpeg",
      "width": "600"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p14"
    }
  }), _c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 2.2.")]), _vm._v(" Correlations between party identification and president/presidential candidate thermometers, 1968–2016")]), _vm._v(" "), _c('p', {
    staticClass: "fign"
  }, [_c('i', [_vm._v("Note")]), _vm._v(": The number of correlations averaged is in parentheses.")]), _vm._v(" "), _c('p', {
    staticClass: "figatr"
  }, [_c('i', [_vm._v("Source")]), _vm._v(": ANES Cumulative Data File and ANES 2016 Time Series Study (face-to-face sample).")])]), _vm._v(" "), _c('p', [_vm._v("It is important to note that the correlations between party identification and candidate thermometers have increased steadily over the period covered "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p14"
    }
  }), _vm._v("by the data, growing from an average of .36 in the 1970s, to .40 in the 1980s, to .46 in the 1990s, to .56 in the 2000s, and to .65 in the 2010s."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chijacobson0023#fn13",
      "id": "fn13r"
    }
  }, [_vm._v("13")]), _vm._v(" The correlations for every subcategory of candidate also grew significantly over time, except among newcomers prior to their first campaign. This is the first of many findings presented in this book documenting the growing partisan coherence in the American electorate over the years between Nixon and Obama.")]), _vm._v(" "), _c('p', [_vm._v("The data in figures "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0201"
    }
  }, [_vm._v("2.1")]), _vm._v(" and "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0202"
    }
  }, [_vm._v("2.2")]), _vm._v(" confirm that the election context strengthens the links between partisanship and affect toward political leaders, especially for those who are not already familiar to most citizens. A more detailed picture of how this occurs during election years is provided by the NAES rolling cross-sectional studies of the 2000, 2004, and 2008 elections. In 2000 ("), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0203a"
    }
  }, [_vm._v("figure 2.3a")]), _vm._v("), the correlation between party identification and candidate affect rose during the campaign for both Al Gore and George W. Bush; it was lower for Bush, the newcomer, at the beginning but soon caught up, equaling Gore’s correlation in November, the high point for both candidates as well as for the departing president, Bill Clinton. In 2004 ("), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0203b"
    }
  }, [_vm._v("figure 2.3b")]), _vm._v("), the correlation for John Kerry was relatively low until after the Iowa caucuses in January, which made him the clear frontrunner, after which it "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p15"
    }
  }), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p16"
    }
  }), _vm._v("increased sharply and continued to rise until October, when it reached its highest point. The correlation was high for the incumbent Bush all along but still increased a bit to reach its peak in November.")]), _vm._v(" "), _c('figure', {
    staticClass: "figure",
    attrs: {
      "id": "fig0203a"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('img', {
    attrs: {
      "alt": "",
      "height": "378",
      "src": "images/chi-jacobson-fig02003a.jpeg",
      "width": "600"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p15"
    }
  }), _c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 2.3a.")]), _vm._v(" Correlations between party identification and favorable views of Bush, Gore, and Clinton, December 1999–January 2001")]), _vm._v(" "), _c('p', {
    staticClass: "figatr"
  }, [_c('i', [_vm._v("Source")]), _vm._v(": NAES 2000.")])]), _vm._v(" "), _c('figure', {
    staticClass: "figure",
    attrs: {
      "id": "fig0203b"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('img', {
    attrs: {
      "alt": "",
      "height": "365",
      "src": "images/chi-jacobson-fig02003b.jpeg",
      "width": "600"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p15"
    }
  }), _c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 2.3b.")]), _vm._v(" Correlations between party identification and favorable views of Bush and Kerry, October 2003–November 2004")]), _vm._v(" "), _c('p', {
    staticClass: "figatr"
  }, [_c('i', [_vm._v("Source")]), _vm._v(": NAES 2004.")])]), _vm._v(" "), _c('figure', {
    staticClass: "figure",
    attrs: {
      "id": "fig0203c"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('img', {
    attrs: {
      "alt": "",
      "height": "373",
      "src": "images/chi-jacobson-fig02003c.jpeg",
      "width": "600"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p16"
    }
  }), _c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 2.3c.")]), _vm._v(" Correlations between party identification and favorable views of Obama, McCain, H. Clinton, and Bush, December 2007–November 2008")]), _vm._v(" "), _c('p', {
    staticClass: "figatr"
  }, [_c('i', [_vm._v("Source")]), _vm._v(": NAES 2008.")])]), _vm._v(" "), _c('p', [_vm._v("In 2008 ("), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0203c"
    }
  }, [_vm._v("figure 2.3c")]), _vm._v("), favorability toward newcomers Barack Obama and John McCain was not strongly related to partisanship prior to the election year. The relationship was particularly weak for McCain, who was initially viewed relatively favorably by Democrats as well as Republicans ("), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0204"
    }
  }, [_vm._v("figure 2.4")]), _vm._v("). That changed over the course of the campaign, with the correlations increasing rather steadily for both candidates to reach their highest levels in November. As in 2004, the correlation for Bush began high but still increased a bit during the election year. The correlation for Hillary Clinton, in contrast, was high early on, when she was the frontrunner (Jacobson 2009b) and had been in the national eye for years, but weakened somewhat as Obama moved ahead of her in the polls and became the party’s nominee and thus principal public face. The growing strength of the connection between partisanship and candidate affect reflected partisans’ increasingly polarized opinions of the contestants ("), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0204"
    }
  }, [_vm._v("figure 2.4")]), _vm._v("). The same was true for 2000 and 2004; presidential campaigns are inevitably polarizing events.")]), _vm._v(" "), _c('figure', {
    staticClass: "figure",
    attrs: {
      "id": "fig0204"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('img', {
    attrs: {
      "alt": "",
      "height": "373",
      "src": "images/chi-jacobson-fig02004.jpeg",
      "width": "600"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p17"
    }
  }), _c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 2.4.")]), _vm._v(" Favorability of partisans toward Obama and McCain, December 2007–November 2008")]), _vm._v(" "), _c('p', {
    staticClass: "figatr"
  }, [_c('i', [_vm._v("Source")]), _vm._v(": NAES 2008.")])]), _vm._v(" "), _c('p', [_vm._v("The ANES 2008–2009 Panel Study tells the same story ("), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0205"
    }
  }, [_vm._v("figure 2.5")]), _vm._v("). The correlations between party identification and thermometer ratings of "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p17"
    }
  }), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p18"
    }
  }), _vm._v("Obama and McCain began at comparatively low levels but grew similarly over the course of the campaign. The initial correlations for Bush and Clinton began a high level; Bush’s stayed there but Clinton’s diminished slightly once she had lost the nomination."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chijacobson0023#fn14",
      "id": "fn14r"
    }
  }, [_vm._v("14")]), _vm._v(" This study also included waves taken after the election in May and July 2009. The correlations for ex-president Bush, Obama, and Clinton, who joined the Obama administration as secretary of state, remained high months after the election; but the correlation for McCain had by May 2009 dropped back to its February 2008 level, largely because ordinary Democrats had reverted to a more positive view of the senator once he was no longer the Republican standard-bearer.")]), _vm._v(" "), _c('figure', {
    staticClass: "figure",
    attrs: {
      "id": "fig0205"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('img', {
    attrs: {
      "alt": "",
      "height": "336",
      "src": "images/chi-jacobson-fig02005.jpeg",
      "width": "600"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p17"
    }
  }), _c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 2.5.")]), _vm._v(" Correlations between party identification and thermometer ratings of Obama, H. Clinton, Bush, and McCain, 2008–2009")]), _vm._v(" "), _c('p', {
    staticClass: "figatr"
  }, [_c('i', [_vm._v("Source")]), _vm._v(": ANES Panel 2008–2009.")])]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0204"
    }
  }, [_vm._v("Candidates, Presidents, and Affect toward their Parties")]), _vm._v(" "), _c('p', {
    staticClass: "paft"
  }, [_vm._v("The data examined so far leave no doubt that the partisan biases arising from identification with a party increasingly shape affective responses to prospective and actual presidential candidates as their involvement in electoral politics deepens; it also appears that party influence wanes when their time in office or as candidates has passed. This section investigates how such variations in affect toward candidates are linked to affective evaluations of their parties. Three of the data sets examined in the previous section also provide measures of affect toward the parties on the same scales used for the political figures. In every instance the party ratings are of course strongly influenced by partisanship, but they are by no means entirely the product of partisan bias. The correlations are usually substantial but never overwhelming; typically (e.g., 93 percent of the time) the correlation between party ID and party affect falls between .45 and .70."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chijacobson0023#fn15",
      "id": "fn15r"
    }
  }, [_vm._v("15")]), _vm._v(" Plenty of variance in people’s affect toward the parties thus remains to be explained by something other than party identification, including of course affect toward the parties’ titular leaders.")]), _vm._v(" "), _c('p', [_vm._v("As with the influence of party identification on candidate affect—and in the same manner and for the same reasons—the influence of candidate/president affect on party affect should vary systematically over the course of a political career. I explored this possibility using the Gallup data by regressing ratings of the parties (on the 10-point scale) on the ratings of the political "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p19"
    }
  }), _vm._v("figures (on the same scale) in each survey, controlling for party identification. The results are summarized in figures "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0206a"
    }
  }, [_vm._v("2.6a")]), _vm._v(" and "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0206b"
    }
  }, [_vm._v("2.6b")]), _vm._v(", which display the mean coefficients from regressing party affect on politicians’ ratings at varying stages of their careers. Several patterns stand out. First, the average regression coefficient is invariably larger in years when the politician is a candidate for national office, often substantially so. Second, coefficients are smallest prior to the first candidacy, and second smallest after the political career is over. The average coefficient for sitting presidents in nonelection years is lower than when they are on the ballot but still quite substantial. Richard Nixon’s career pattern is particularly revealing. His influence on party affect peaks with his 1960 candidacy, drops off when he is out of office, then once again reaches a high point during his 1972 reelection campaign, before falling off again when he is no longer president.")]), _vm._v(" "), _c('figure', {
    staticClass: "figure",
    attrs: {
      "id": "fig0206a"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('img', {
    attrs: {
      "alt": "",
      "height": "349",
      "src": "images/chi-Jacobson-fig02006a-NEW.jpeg",
      "width": "600"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p19"
    }
  }), _c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 2.6a.")]), _vm._v(" Effect of presidential or presidential candidate affect on party affect, controlling for party identification, Republicans")]), _vm._v(" "), _c('p', {
    staticClass: "fign"
  }, [_c('i', [_vm._v("Note")]), _vm._v(": The number of surveys averaged is in parentheses.")]), _vm._v(" "), _c('p', {
    staticClass: "figatr"
  }, [_c('i', [_vm._v("Source")]), _vm._v(": Gallup Polls.")])]), _vm._v(" "), _c('figure', {
    staticClass: "figure",
    attrs: {
      "id": "fig0206b"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('img', {
    attrs: {
      "alt": "",
      "height": "352",
      "src": "images/chi-Jacobson-fig02006b-NEW.jpeg",
      "width": "600"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p20"
    }
  }), _c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 2.6b.")]), _vm._v(" Effect of presidential or presidential candidate affect on party affect, controlling for party identification, Democrats")]), _vm._v(" "), _c('p', {
    staticClass: "fign"
  }, [_c('i', [_vm._v("Note")]), _vm._v(": The number of surveys averaged is in parentheses.")]), _vm._v(" "), _c('p', {
    staticClass: "figatr"
  }, [_c('i', [_vm._v("Source")]), _vm._v(": Gallup Polls.")])]), _vm._v(" "), _c('p', [_vm._v("Every career sequence depicted in these charts supports the idea that partisan political figures’ position in the electoral career cycle determines the degree to which citizens’ feelings about them influence feelings about their parties. These patterns are summarized in "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0207"
    }
  }, [_vm._v("figure 2.7")]), _vm._v(", which displays the mean regression coefficients for political figures according to stage of electoral career (controlling for party identification) plus mean coefficients for each partisan subgroup separately. The context has a large effect on the extent to which candidate/president affect influences party affect. Its "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p20"
    }
  }), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p21"
    }
  }), _vm._v("influence grows as the election approaches, peaking after Labor Day. The effect for sitting presidents is about the same as that registered early in the election year, and it shrinks notably when they are no longer in office. The coefficients for partisans of all stripes shift similarly in response to the context, but the effects are almost always substantially larger for opposition partisans than for the candidate/president’s partisans."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chijacobson0023#fn16",
      "id": "fn16r"
    }
  }, [_vm._v("16")]), _vm._v(" That is, opposition partisans’ feelings toward a party are considerably more sensitive to their feelings about its leaders than are those of the leader’s partisans.")]), _vm._v(" "), _c('figure', {
    staticClass: "figure",
    attrs: {
      "id": "fig0207"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('img', {
    attrs: {
      "alt": "",
      "height": "338",
      "src": "images/chi-Jacobson-fig02007-NEW.jpeg",
      "width": "600"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p20"
    }
  }), _c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 2.7.")]), _vm._v(" Effect of ratings of candidates/presidents on ratings of their parties, by status and party identification")]), _vm._v(" "), _c('p', {
    staticClass: "fign"
  }, [_c('i', [_vm._v("Note")]), _vm._v(": The number of Gallup surveys used to compute the average regression coefficient is in parentheses; the coefficients for all respondents are estimated with controls for party identification.")])]), _vm._v(" "), _c('p', [_vm._v("The ANES time series offers a second perspective on these relationships. The mean coefficient from regressing party thermometer on candidate thermometer, controlling for party identification, appears in "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0208"
    }
  }, [_vm._v("figure 2.8")]), _vm._v(". Again we see that the relationship between candidate/president affect and party affect is lowest in years before candidates win the nomination and after they are no longer running. The coefficients are much larger in years when they are on the ballot and largest when presidents are seeking reelection. In years when the president is in office but not on the ballot, the coefficient is slightly smaller. Partisans again respond similarly to the electoral context, with the party affect among the rival party’s identifiers again being on average more sensitive to variations in candidate/president affect, although "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p22"
    }
  }), _vm._v("the differences are smaller than in the Gallup data, particularly when the candidate is on the ballot (results not shown).")]), _vm._v(" "), _c('figure', {
    staticClass: "figure",
    attrs: {
      "id": "fig0208"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('img', {
    attrs: {
      "alt": "",
      "height": "321",
      "src": "images/chi-jacobson-fig02008.jpeg",
      "width": "600"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p21"
    }
  }), _c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 2.8.")]), _vm._v(" Effect of presidents / presidential candidates’ thermometer ratings on party thermometer ratings, controlling for party identification, 1978–2016")]), _vm._v(" "), _c('p', {
    staticClass: "fign"
  }, [_c('i', [_vm._v("Note")]), _vm._v(": The number of coefficients averaged is in parentheses.")]), _vm._v(" "), _c('p', {
    staticClass: "figatr"
  }, [_c('i', [_vm._v("Source")]), _vm._v(": ANES cumulative data file and ANES 2016 time series (face-to-face sample).")])]), _vm._v(" "), _c('p', [_vm._v("The ANES 2008–2009 panel study offers a more detailed account of how the link between candidate and party affect tightens as candidates pursue and win the nomination. At the simplest level, "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0209"
    }
  }, [_vm._v("figure 2.9")]), _vm._v(" displays the increasing correlation between the two among partisans of all stripes from over the course of the 2008 election year. The increase is especially striking among Democrats, rising from .22 to .62 for Obama and the Democratic Party, and from .28 to .59 (after peaking at .64 in September) for McCain and the Republican Party.")]), _vm._v(" "), _c('figure', {
    staticClass: "figure",
    attrs: {
      "id": "fig0209"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('img', {
    attrs: {
      "alt": "",
      "height": "342",
      "src": "images/chi-jacobson-fig02009.jpeg",
      "width": "600"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p22"
    }
  }), _c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 2.9.")]), _vm._v(" Correlations between candidate and party affect, by party identification, 2008")]), _vm._v(" "), _c('p', {
    staticClass: "figatr"
  }, [_c('i', [_vm._v("Source")]), _vm._v(": ANES 2008–2009 Panel Study.")])]), _vm._v(" "), _c('p', [_vm._v("Over the same time, the "), _c('i', [_vm._v("relative")]), _vm._v(" impact of party identification and candidate affect on party affect shifted sharply as well. The partial regression coefficients displayed in "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0210"
    }
  }, [_vm._v("figure 2.10")]), _vm._v(" indicate that party identification dominated party affect at the beginning of the election year but over the course of the campaign gradually ceded that dominance to presidential candidate affect. Observe also that the new balance remained in place after Obama’s first seven months in office (he of course remained his party’s most prominent figure by far). Even as the relationship between party identification and candidate affect tightens ("), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0205"
    }
  }, [_vm._v("figure 2.5")]), _vm._v("), then, feelings about the candidates have an increasingly powerful independent effect on feelings about their parties.")]), _vm._v(" "), _c('figure', {
    staticClass: "figure",
    attrs: {
      "id": "fig0210"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('img', {
    attrs: {
      "alt": "",
      "height": "361",
      "src": "images/chi-jacobson-fig02010.jpeg",
      "width": "600"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p23"
    }
  }), _c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 2.10.")]), _vm._v(" Relative impact of party identification and candidate affect on party affect, January 2008-July 2009")]), _vm._v(" "), _c('p', {
    staticClass: "figatr"
  }, [_c('i', [_vm._v("Source")]), _vm._v(": ANES Panel 2008–2009.")])]), _vm._v(" "), _c('p', [_vm._v("The ANES panel also documents a shift in the relative impact on party affect of the presidential candidates compared to that of initially more "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p23"
    }
  }), _vm._v("prominent leaders in their respective parties ("), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0211"
    }
  }, [_vm._v("figure 2.11")]), _vm._v("). Of the two major Democratic competitors for the 2008 nomination, Hillary Clinton was much the better known at the beginning of the campaign, and in January, respondents’ feelings about her had a notably larger influence on feelings about the Democratic Party than did feelings about Obama (party identification controlled). By June that was no longer the case, as feelings about Obama had become much more influential. A similar pattern occurred on the Republican side; initially, affect toward President Bush had a much greater influence on affect toward his party, but after McCain had become the party’s choice, he supplanted Bush as the greater influence.")]), _vm._v(" "), _c('figure', {
    staticClass: "figure",
    attrs: {
      "id": "fig0211"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('img', {
    attrs: {
      "alt": "",
      "height": "356",
      "src": "images/chi-jacobson-fig02011.jpeg",
      "width": "600"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p24"
    }
  }), _c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 2.11.")]), _vm._v(" Relative impact of candidate/president affect on party affect, controlling for party identification, January–October 2008")]), _vm._v(" "), _c('p', {
    staticClass: "figatr"
  }, [_c('i', [_vm._v("Source")]), _vm._v(": ANES Panel 2008–2009.")])]), _vm._v(" "), _c('p', [_vm._v("The panel structure of this study opens yet another window for observing the influence of candidate affect on party affect by allowing examination of how feelings about the parties changed in response to changes in feelings about the candidates over the course of the campaign. Tables "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#th0201a"
    }
  }, [_vm._v("2.1a")]), _vm._v(" and "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#th0201b"
    }
  }, [_vm._v("2.1b")]), _vm._v(" report the results of regressing changes in party ratings between pairs of waves on changes in candidate ratings, controlling for lagged candidate and party ratings and party identification."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chijacobson0023#fn17",
      "id": "fn17r"
    }
  }, [_vm._v("17")]), _vm._v(" In every case, changes in candidate "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p24"
    }
  }), _vm._v("affect have a substantial effect on changes in party affect; lagged candidate affect is almost as influential. The middle column in the second row of equations in each table shows the results for net changes between the first (January) and last (October) wave of interviews during the election year."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chijacobson0023#fn18",
      "id": "fn18r"
    }
  }, [_vm._v("18")]), _vm._v(" The coefficients indicate that change in affect toward Obama had a greater effect on changes in affect toward the Democratic Party than was the case for McCain and his party over the full course of the election year. Estimates from the additional survey wave taken in May 2009 show that changes in feelings about the Democratic Party echoed changes in feelings about Obama after he took office as well as during the campaign.")]), _vm._v(" "), _c('p', [_vm._v("Tables "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#th0201a"
    }
  }, [_vm._v("2.1a")]), _vm._v(" and "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#th0201b"
    }
  }, [_vm._v("2.1b")]), _vm._v(" also include equations comparing the influence of Obama and Clinton on changes in feelings about the Democratic Party, and McCain and Bush on changes in feelings about the Republican Party, over the election year (the data on Bush run through the October wave; Clinton "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p25"
    }
  }), _vm._v("last appears in the September wave). Obama’s influence was more than three times as large as Clinton’s (in both the change and lagged variables); the balance on the Republican side was more even, not especially surprising given Bush’s status as president and the leading decision maker during the financial crisis that erupted in September 2008.")]), _vm._v(" "), _c('p', {
    staticClass: "th",
    attrs: {
      "id": "th0201a"
    }
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p25"
    }
  }), _c('span', {
    staticClass: "thn"
  }, [_vm._v("Table 2.1a.")]), _vm._v(" Change in Democratic Party affect as a function of change in Democratic candidate affect")]), _vm._v(" "), _c('table', [_c('thead', [_c('tr', [_c('td'), _vm._v(" "), _c('th', {
    staticClass: "tch",
    staticStyle: {
      "text-align": "center"
    },
    attrs: {
      "colspan": "6"
    }
  }, [_c('span', {
    staticClass: "grc"
  }, [_vm._v("Δ")]), _vm._v(" Democratic Party affect")])]), _vm._v(" "), _c('tr', [_c('td'), _vm._v(" "), _c('th', {
    staticClass: "tch1",
    attrs: {
      "colspan": "2"
    }
  }, [_vm._v("January–February 2008")]), _vm._v(" "), _c('th', {
    staticClass: "tch1",
    attrs: {
      "colspan": "2"
    }
  }, [_vm._v("February–June 2008")]), _vm._v(" "), _c('th', {
    staticClass: "tch1",
    attrs: {
      "colspan": "2"
    }
  }, [_vm._v("June–September 2008")])])]), _vm._v(" "), _c('tbody', [_c('tr', [_c('td', [_c('p', {
    staticClass: "tdf"
  }, [_c('span', {
    staticClass: "grc"
  }, [_vm._v("Δ")]), _vm._v(" Obama affect")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdf"
  }, [_vm._v(".20***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdf"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdf"
  }, [_vm._v(".31***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdf"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdf"
  }, [_vm._v(".31***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdf"
  }, [_vm._v(".04")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Obama affect, t–1")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".16***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".27***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".28***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Democratic Party affect, t–1")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.46***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.55***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.50***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Party identification, t")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".22***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".21***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".14***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Constant")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".09*")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".11***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.41***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Adjusted R"), _c('sup', [_vm._v("2")])])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".25")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".33")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".31")])]), _vm._v(" "), _c('td')]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "tdl"
  }, [_vm._v("N")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdl"
  }, [_vm._v("1374")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdl"
  }, [_vm._v("1229")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdl"
  }, [_vm._v("1275")])]), _vm._v(" "), _c('td')]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "line",
    attrs: {
      "colspan": "7"
    }
  })]), _vm._v(" "), _c('tr', [_c('td'), _vm._v(" "), _c('th', {
    staticClass: "tch1",
    attrs: {
      "colspan": "2"
    }
  }, [_vm._v("September–October 2008")]), _vm._v(" "), _c('th', {
    staticClass: "tch1",
    attrs: {
      "colspan": "2"
    }
  }, [_vm._v("January–October 2008")]), _vm._v(" "), _c('th', {
    staticClass: "tch1",
    attrs: {
      "colspan": "2"
    }
  }, [_vm._v("October 2008–July 2009")])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "tdf"
  }, [_c('span', {
    staticClass: "symb"
  }, [_vm._v("Δ")]), _vm._v(" Obama affect")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdf"
  }, [_vm._v(".31***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdf"
  }, [_vm._v(".03")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdf"
  }, [_vm._v(".43***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdf"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdf"
  }, [_vm._v(".39***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdf"
  }, [_vm._v(".03")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Obama affect, t–1")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".23***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".39***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".35***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Democratic Party affect, t–1")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.54***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.73***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.68***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Party identification, t")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".19***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".02")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".22***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".21***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".02")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Constant")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.51***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".07")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.58***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".11")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.81***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".07")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Adjusted R"), _c('sup', [_vm._v("2")])])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".31")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".48")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".45")])]), _vm._v(" "), _c('td')]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "tdl"
  }, [_vm._v("N")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdl"
  }, [_vm._v("2453")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdl"
  }, [_vm._v("1367")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdl"
  }, [_vm._v("1275")])]), _vm._v(" "), _c('td')]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "line",
    attrs: {
      "colspan": "7"
    }
  })]), _vm._v(" "), _c('tr', [_c('td'), _vm._v(" "), _c('th', {
    staticClass: "tch1",
    attrs: {
      "colspan": "2"
    }
  }, [_vm._v("January–September 2008")]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td')]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_c('span', {
    staticClass: "grc"
  }, [_vm._v("Δ")]), _vm._v(" Obama affect")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".33***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td')]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_c('span', {
    staticClass: "grc"
  }, [_vm._v("Δ")]), _vm._v(" Clinton affect")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".10**")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td')]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Obama affect, t–1")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".36***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td')]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Clinton affect, t–1")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".09**")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td')]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Democratic Party affect, t–1")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.68***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td')]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Party identification, t")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".19***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td')]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Constant")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".52***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td')]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Adjusted R"), _c('sup', [_vm._v("2")])])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".43")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td')]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "tdl"
  }, [_vm._v("N")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdl"
  }, [_vm._v("1356")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td')])])]), _vm._v(" "), _c('p', {
    staticClass: "tn"
  }, [_c('i', [_vm._v("Note")]), _vm._v(": First entry in each column is the regression coefficient; the second entry is the standard error.")]), _vm._v(" "), _c('p', {
    staticClass: "tatr"
  }, [_c('i', [_vm._v("Source")]), _vm._v(": ANES Panel 2008–2009.")]), _vm._v(" "), _c('p', {
    staticClass: "tn"
  }, [_vm._v("*p<.05; **p<.01; ***p<.001.")]), _vm._v(" "), _c('p', {
    staticClass: "th",
    attrs: {
      "id": "th0201b"
    }
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p26"
    }
  }), _c('span', {
    staticClass: "thn"
  }, [_vm._v("Table 2.1b.")]), _vm._v(" Change in Republican Party affect as a function of change in Republican candidate affect")]), _vm._v(" "), _c('table', [_c('thead', [_c('tr', [_c('td'), _vm._v(" "), _c('th', {
    staticClass: "tch",
    staticStyle: {
      "text-align": "center"
    },
    attrs: {
      "colspan": "6"
    }
  }, [_c('span', {
    staticClass: "grc"
  }, [_vm._v("Δ")]), _vm._v(" Republican Party affect")])]), _vm._v(" "), _c('tr', [_c('td'), _vm._v(" "), _c('th', {
    staticClass: "tch1",
    attrs: {
      "colspan": "2"
    }
  }, [_vm._v("January–February 2008")]), _vm._v(" "), _c('th', {
    staticClass: "tch1",
    attrs: {
      "colspan": "2"
    }
  }, [_vm._v("February–June 2008")]), _vm._v(" "), _c('th', {
    staticClass: "tch1",
    attrs: {
      "colspan": "2"
    }
  }, [_vm._v("June–September 2008")])])]), _vm._v(" "), _c('tbody', [_c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_c('span', {
    staticClass: "grc"
  }, [_vm._v("Δ")]), _vm._v(" McCain affect")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".21***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".35***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".39***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("McCain affect, t–1")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".23***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".28***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".31***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Republican Party affect, t–1")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.50***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.58***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.60***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Party identification, t")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.18***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.23***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.23***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Constant")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.07")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.15***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".59***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".09")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Adjusted R"), _c('sup', [_vm._v("2")])])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".25")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".36")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".37")])]), _vm._v(" "), _c('td')]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "tdl"
  }, [_vm._v("N")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdl"
  }, [_vm._v("1373")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdl"
  }, [_vm._v("1230")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdl"
  }, [_vm._v("1275")])]), _vm._v(" "), _c('td')]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "line",
    attrs: {
      "colspan": "7"
    }
  })]), _vm._v(" "), _c('tr', [_c('td'), _vm._v(" "), _c('th', {
    staticClass: "tch1",
    attrs: {
      "colspan": "2"
    }
  }, [_vm._v("September–October 2008")]), _vm._v(" "), _c('th', {
    staticClass: "tch1",
    attrs: {
      "colspan": "2"
    }
  }, [_vm._v("January–October 2008")]), _vm._v(" "), _c('th', {
    staticClass: "tch1",
    attrs: {
      "colspan": "2"
    }
  }, [_vm._v("January–September 2008")])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_c('span', {
    staticClass: "grc"
  }, [_vm._v("Δ")]), _vm._v(" McCain affect")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".29***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".33***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".24***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("McCain affect, t–1")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".19***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".23***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".18**")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_c('span', {
    staticClass: "grc"
  }, [_vm._v("Δ")]), _vm._v(" Bush affect")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".17***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Bush affect, t–1")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".19***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("McCain affect, t–1")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".19***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".23***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".18**")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Republican Party affect, t–1")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.49***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.60***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.68***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".04")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Party identification, t")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.16***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".02")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.20***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.16***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".03")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Constant")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".40***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".07")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".58***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".10")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".59***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".10")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("Adjusted R"), _c('sup', [_vm._v("2")])])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".31")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".37")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".41")])]), _vm._v(" "), _c('td')]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "tdl"
  }, [_vm._v("N")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdl"
  }, [_vm._v("2454")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdl"
  }, [_vm._v("1246")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdl"
  }, [_vm._v("1245")])]), _vm._v(" "), _c('td')])])]), _vm._v(" "), _c('p', {
    staticClass: "tn"
  }, [_c('i', [_vm._v("Note")]), _vm._v(": First entry in each column is the regression coefficient; the second entry is the standard error.")]), _vm._v(" "), _c('p', {
    staticClass: "tn"
  }, [_c('i', [_vm._v("Source")]), _vm._v(": ANES Panel 2008–2009.")]), _vm._v(" "), _c('p', {
    staticClass: "tn"
  }, [_vm._v("***p<.001.")]), _vm._v(" "), _c('p', [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p26"
    }
  }), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0211"
    }
  }, [_vm._v("Figure 2.11")]), _vm._v(" reveals how the new Republican champion, McCain, gradually eclipsed the old one, Bush, as the greater influence on people’s feelings about the Republican Party. The pattern in which a president’s influence wanes while that of his successor as party leader grows is also found in the Gallup and ANES time series data. The most striking evidence of this process is provided by the ten Gallup surveys taken between 1956 and 1968 that included the scalometer ratings for Dwight Eisenhower, Richard Nixon, and the Republican Party ("), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0212"
    }
  }, [_vm._v("figure 2.12")]), _vm._v("). Leading up to the 1956 election, Eisenhower’s ratings had the greater impact, although Nixon’s became increasingly influential as the election approached. In 1960, when Nixon was the presidential candidate and Eisenhower the lame duck, Nixon’s ratings were the more influential, especially in surveys taken after Labor Day. Once Nixon was defeated and out of public office, however, Eisenhower "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p27"
    }
  }), _vm._v("reemerged as the more important party referent—until 1968, when Nixon was in the process of making his ultimately successful comeback.")]), _vm._v(" "), _c('figure', {
    staticClass: "figure",
    attrs: {
      "id": "fig0212"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('img', {
    attrs: {
      "alt": "",
      "height": "354",
      "src": "images/chi-jacobson-fig02012.jpeg",
      "width": "600"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p27"
    }
  }), _c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 2.12.")]), _vm._v(" Relative effect of Eisenhower and Nixon affect on Republican Party affect, 1956–1968, controlling for party identification")]), _vm._v(" "), _c('p', {
    staticClass: "figatr"
  }, [_c('i', [_vm._v("Source")]), _vm._v(": Gallup Polls.")])]), _vm._v(" "), _c('p', [_c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0213"
    }
  }, [_vm._v("Figure 2.13")]), _vm._v(" provides additional evidence from the Gallup data that the relative salience of leaders, a product of context, determines the extent to which they influence affect toward their parties. The first two comparisons are between coefficients for Eisenhower and Barry Goldwater during and after the latter’s failed bid for the White House. Affect toward Goldwater largely determined affect toward the Republican Party during the election season, but within six months of his defeat, affect toward Eisenhower had the greater influence. The relative influence of feelings about Nixon and Ronald Reagan is traced out in the other six surveys. In determining party affect, Nixon overshadowed Reagan in 1966, 1967, and especially 1968; both were overshadowed by Gerald Ford while he sat in the White House. Subsequently, Reagan totally eclipsed Nixon, much to the benefit of his party, for opinions of Nixon were extraordinarily unfavorable in the years following his 1974 resignation to avoid impeachment in the wake of the Watergate scandal.")]), _vm._v(" "), _c('figure', {
    staticClass: "figure",
    attrs: {
      "id": "fig0213"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('img', {
    attrs: {
      "alt": "",
      "height": "358",
      "src": "images/chi-jacobson-fig02013.jpeg",
      "width": "600"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p28"
    }
  }), _c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 2.13.")]), _vm._v(" Relative effect of Republican leaders’ affect on party affect, controlling for party identification")]), _vm._v(" "), _c('p', {
    staticClass: "figatr"
  }, [_c('i', [_vm._v("Source")]), _vm._v(": Gallup Polls.")])]), _vm._v(" "), _c('p', [_vm._v("The ANES time series data also show how presidents relinquish their influence over party affect to their successors as party leaders. As before, affect is measured by thermometer scores, and the entries in figures "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0214"
    }
  }, [_vm._v("2.14")]), _vm._v(" and "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0215"
    }
  }, [_vm._v("2.15")]), _vm._v(" are the regression coefficients on the president/candidate thermometers (with party identification controlled). In all four cases, the affect toward the "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p28"
    }
  }), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p29"
    }
  }), _vm._v("party’s current candidate replaces affect toward the president as the stronger influence on party affect. The shift is gradual for sitting vice presidents Al Gore and George H. W. Bush, presumably because they were obvious heirs apparent who became increasing foci of political attention during the president’s second term.")]), _vm._v(" "), _c('figure', {
    staticClass: "figure",
    attrs: {
      "id": "fig0214"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('img', {
    attrs: {
      "alt": "",
      "height": "353",
      "src": "images/chi-jacobson-fig02014.jpeg",
      "width": "600"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p28"
    }
  }), _c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 2.14.")]), _vm._v(" Relative effect of democratic president/candidate thermometer on Democratic Party thermometer, controlling for party identification")]), _vm._v(" "), _c('p', {
    staticClass: "figatr"
  }, [_c('i', [_vm._v("Source")]), _vm._v(": ANES Cumulative Data File.")])]), _vm._v(" "), _c('figure', {
    staticClass: "figure",
    attrs: {
      "id": "fig0215"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('img', {
    attrs: {
      "alt": "",
      "height": "335",
      "src": "images/chi-jacobson-fig02015.jpeg",
      "width": "600"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p29"
    }
  }), _c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 2.15.")]), _vm._v(" Relative effect of Republican president/candidate thermometers on Republican Party thermometer, controlling for party identification")]), _vm._v(" "), _c('p', {
    staticClass: "figatr"
  }, [_c('i', [_vm._v("Source")]), _vm._v(": ANES Cumulative Data File.")])]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0205"
    }
  }, [_vm._v("The Other Party")]), _vm._v(" "), _c('p', {
    staticClass: "paft"
  }, [_vm._v("Feelings about presidential candidates and presidents strongly influence feelings about their parties. How do they affect feelings about the other party? Specifically, do more positive view of candidates generate more negative views of the rival party? The answer is generally no except during the post–Labor Day period, when partisan sentiments peak. A summary of the Gallup results comparable to those displayed for all respondents in "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0207"
    }
  }, [_vm._v("figure 2.7")]), _vm._v(" shows that when party ID is controlled for, affect toward presidents or presidential candidates usually had little or no systematic effect on affect toward the rival party. The mean regression coefficient for the preelection year period was actually positive (.04), and none was significantly negative. The average in pre–Labor Day election-year surveys was −.02; four of the ten were negative and statistically significant (at p<.05), all of them from surveys taken in the 1950s. The average coefficients for presidents in office and for "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p30"
    }
  }), _vm._v("former presidents and candidates were also tiny (.01 and −.04 respectively), and only seven of thirty-three were negative and statistically significant (four of them for Truman, two for Herbert Hoover). In the surveys taken in the election year after Labor Day, however, the average coefficient was −.12 (s.d.=.08, range .02 to −.23), and nine of the twelve were statistically significant (p<.05). The coefficients were largest (−.17 to −.23) in 1964 and 1984, the most ideologically divisive contests during the 1956–84 period covered by the Gallup data.")]), _vm._v(" "), _c('p', [_vm._v("I also estimated the effects of the presidential candidate thermometer ratings on rival party ratings in the ANES time series (1980–2016). The mean coefficient was −.05 (s.d.=.06, range .04 to −.16; about half were significant at p<.05), compared with the mean coefficient for the candidates’ own parties of .46 (s.d.=.05, range .25 to .56). The (negative) effects were strongest for Reagan (1984), Kerry (2004), Obama (2012), and Trump (2016). I also regressed the party thermometers on both candidates’ thermometers at the same time, controlling for party ID, and in every case the party’s candidate’s ratings produced by far the larger coefficient; in none of these regressions did the rival party’s candidate’s thermometer ratings retain a significant negative effect on people’s feelings about a party.")]), _vm._v(" "), _c('p', [_vm._v("Finally, the ANES panel study reveals how the influence of candidate affect on party affect changed over the 2008 election year. The first two columns in "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#th0202"
    }
  }, [_vm._v("table 2.2")]), _vm._v(" report the coefficients from regressing rival party affect on candidate affect (controlling for party identification), in the five election-year panel waves. The other pairs of columns report the regression coefficients when both candidates’ ratings are in the equations. As the election draws closer, positive views of a candidate evidently inspire increasingly "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p31"
    }
  }), _vm._v("negative views of the rival party; still, affect toward parties is clearly dominated by the growing influence of feelings about the party’s own candidate. After the election, Obama’s influence on affect toward the Republican Party fell off again (although his influence on feelings about his own party did not; see "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0011#fig0210"
    }
  }, [_vm._v("figure 2.10")]), _vm._v(").")]), _vm._v(" "), _c('p', {
    staticClass: "th",
    attrs: {
      "id": "th0202"
    }
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "data-locator": "p30"
    }
  }), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chijacobson0006#toc"
    }
  }, [_c('span', {
    staticClass: "thn"
  }, [_vm._v("Table 2.2.")]), _vm._v(" Coefficients from regressions of party affect on candidate affect over the 2008 election year")])]), _vm._v(" "), _c('table', [_c('thead', [_c('tr', [_c('th', {
    staticClass: "tch",
    attrs: {
      "rowspan": "2"
    }
  }), _vm._v(" "), _c('th', {
    staticClass: "tch",
    attrs: {
      "rowspan": "2"
    }
  }, [_vm._v("McCain, Democratic Party")]), _vm._v(" "), _c('th', {
    staticClass: "tch",
    attrs: {
      "rowspan": "2"
    }
  }, [_vm._v("Obama, Republican Party")]), _vm._v(" "), _c('th', {
    staticClass: "tch",
    attrs: {
      "colspan": "2"
    }
  }, [_vm._v("Republican Party")]), _vm._v(" "), _c('th', {
    staticClass: "tch",
    attrs: {
      "colspan": "2"
    }
  }, [_vm._v("Democratic Party")])]), _vm._v(" "), _c('tr', [_c('th', {
    staticClass: "tch"
  }, [_vm._v("McCain")]), _vm._v(" "), _c('th', {
    staticClass: "tch"
  }, [_vm._v("Obama")]), _vm._v(" "), _c('th', {
    staticClass: "tch"
  }, [_vm._v("McCain")]), _vm._v(" "), _c('th', {
    staticClass: "tch"
  }, [_vm._v("Obama")])])]), _vm._v(" "), _c('tbody', [_c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("January")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".00")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.05")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".19***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.07**")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.02")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".19***")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("February")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.02")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.07*")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".34***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.05")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".00")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".27***")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("June")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.10*")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.10***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".44***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.06**")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.05")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".38***")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("September")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.22***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.23***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".49***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.11***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.07*")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".46***")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("October")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.18***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.19***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".47***")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.07**")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v("–.02")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "td"
  }, [_vm._v(".59***")])])]), _vm._v(" "), _c('tr', [_c('td', [_c('p', {
    staticClass: "tdl"
  }, [_vm._v("July 2009")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "tdl"
  }, [_vm._v("–.10**")])]), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td'), _vm._v(" "), _c('td')])])]), _vm._v(" "), _c('p', {
    staticClass: "tn"
  }, [_c('i', [_vm._v("Note")]), _vm._v(": All coefficients were estimated with controls for party identification.")]), _vm._v(" "), _c('p', {
    staticClass: "tatr"
  }, [_c('i', [_vm._v("Source")]), _vm._v(": ANES Panel 2008–2009.")]), _vm._v(" "), _c('p', {
    staticClass: "tn"
  }, [_vm._v("*p<.05; **p<.01; ***p<.001.")]), _vm._v(" "), _c('p', [_vm._v("Taken together, the results in this section suggest that election campaigns generate, activate, and reinforce affectively tagged considerations regarding both candidates and both parties that increasingly enter the mix that people draw from to respond in “affectively congruent” ways to questions about how they feel about the parties. Feelings about candidates shape feelings about both parties, but the influence on the candidates’ own parties are far stronger, presumably because they are more elaborately grounded in networked memories, more obviously relevant, and thus more likely to come to mind and enter the mix when primed by the questions that measure party affect.")]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0206"
    }
  }, [_vm._v("Conclusion")]), _vm._v(" "), _c('p', {
    staticClass: "paft"
  }, [_vm._v("The evidence from these four sets of surveys and across six decades confirms that the influence presidents exert over their party’s popular standing with the public begins before they enter the White House, remains high while they serve, and lingers, albeit with diminished force, after they have departed. A link between party identification and people’s feelings about prospective presidents is present from the start, but it becomes much stronger during the initial election campaign and is further strengthened when a president seeks reelection. Even as this occurs, feelings about presidents have an increasing impact on feelings about their parties over and above feelings generated by partisanship. As an administration draws to a close and its party settles on a new standard-bearer, however, the new leader supplants the old as the stronger influence on how people regard the party, at least until the prospective successor loses. This sequence is readily understood in terms of constructivist theories of attitude formation and expression that emphasize the roles of priming, salience, repetition, and motivated reasoning—all promoted by the campaign environment—in determining the composition of considerations in short-term memory that generate expressed opinions about parties and candidates.")]), _vm._v(" "), _c('p', [_vm._v("This chapter has focused on how affective reactions to parties and presidents have evolved together over the course of electoral careers. The next chapter provides a more detailed look at how popular assessments of a president’s performance while in office affect the feelings about their parties generally and of their congressional wings and their leaders in particular.")])])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-75d1b3ec","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/chi-jacobson-0011.vue
// module id = 84
// module chunks = 0